import { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { api, money, errorMessage } from "../api";
import { LayoutDashboard, Users, Receipt, Settings2, LogOut, Check, Crown, Pencil, X, Loader2, Shield, IndianRupee, Building2 } from "lucide-react";

const CYCLES = [
  { id: "monthly", label: "Monthly" },
  { id: "half_yearly", label: "Half-yearly" },
  { id: "yearly", label: "Annually" },
];

function Nav({ page, setPage, logout }) {
  const items = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "plans", label: "Plans & Pricing", icon: IndianRupee },
    { id: "tenants", label: "Tenants", icon: Building2 },
    { id: "payments", label: "Payments", icon: Receipt },
    { id: "settings", label: "My Account", icon: Settings2 },
  ];
  return (
    <aside className="admin-side">
      <div className="admin-brand">
        <div className="admin-mark"><Shield size={18}/></div>
        <div><b>FynDine</b><small>SUPER ADMIN</small></div>
      </div>
      <nav>
        {items.map(({id, label, icon: Icon}) => (
          <button key={id} className={page === id ? "admin-nav active" : "admin-nav"} onClick={() => setPage(id)} data-testid={`admin-nav-${id}`}>
            <Icon size={15}/> {label}
          </button>
        ))}
      </nav>
      <button className="admin-logout" onClick={logout} data-testid="admin-logout"><LogOut size={14}/> Sign out</button>
    </aside>
  );
}

function Overview() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/overview").then(r => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="admin-loading"><Loader2 className="spin" size={18}/> Loading…</div>;
  return (
    <>
      <div className="admin-head">
        <div><span className="overline">OPERATIONAL OVERVIEW</span><h1>Welcome, Super Admin</h1><p>You are the merchant of record on {data.legal_entity}.</p></div>
        <span className={`admin-chip ${data.payment_mode.startsWith("LIVE") ? "live" : "mock"}`}>{data.payment_mode}</span>
      </div>
      <div className="admin-cards">
        <div className="admin-card"><Users size={18}/><div><small>Tenants</small><strong data-testid="admin-tenants-count">{data.users_count}</strong></div></div>
        <div className="admin-card"><Building2 size={18}/><div><small>Outlets</small><strong>{data.restaurants_count}</strong></div></div>
        <div className="admin-card"><Crown size={18}/><div><small>Active subscriptions</small><strong>{data.active_subscriptions}</strong></div></div>
        <div className="admin-card"><IndianRupee size={18}/><div><small>Gross revenue</small><strong>{money(data.gross_revenue)}</strong></div></div>
      </div>
      <section className="admin-section">
        <h2>Getting started</h2>
        <ol>
          <li>Review and adjust prices for Basic / Professional / Premium on the <b>Plans &amp; Pricing</b> tab. Every rupee you set here is what tenants will see on checkout.</li>
          <li>Payments are currently in <b>{data.payment_mode}</b>. Set <code>RAZORPAY_KEY_ID</code> and <code>RAZORPAY_KEY_SECRET</code> in the backend <code>.env</code> to flip to LIVE.</li>
          <li>Watch tenant signups and billing on the <b>Tenants</b> and <b>Payments</b> tabs.</li>
        </ol>
      </section>
    </>
  );
}

function PlansEditor() {
  const [data, setData] = useState({ plans: [], feature_catalog: [] });
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = () => api.get("/admin/plans").then(r => setData(r.data));
  useEffect(() => { load(); }, []);

  const openEdit = (plan) => { setEditing(plan.id); setDraft({ ...plan, prices: { ...plan.prices } }); setErr(""); };
  const save = async () => {
    setBusy(true); setErr("");
    try {
      await api.put(`/admin/plans/${editing}`, {
        name: draft.name, tagline: draft.tagline, prices: draft.prices,
        features: draft.features, max_branches: Number(draft.max_branches), popular: !!draft.popular,
      });
      setEditing(null); setDraft(null); await load();
    } catch (e) { setErr(errorMessage(e)); }
    finally { setBusy(false); }
  };
  const toggleFeature = (key) => setDraft(d => ({ ...d, features: d.features.includes(key) ? d.features.filter(k => k !== key) : [...d.features, key] }));

  const groups = {};
  for (const f of data.feature_catalog) { (groups[f.group] = groups[f.group] || []).push(f); }

  return (
    <>
      <div className="admin-head">
        <div><span className="overline">PLANS &amp; PRICING</span><h1>Edit any price, feature or limit</h1><p>Changes apply instantly to new checkouts; existing subscriptions keep their original amount until renewal.</p></div>
      </div>
      <div className="plan-admin-grid">
        {data.plans.map((p) => (
          <article key={p.id} className={`plan-admin ${p.popular ? "popular" : ""}`} style={{ borderColor: p.color }} data-testid={`admin-plan-${p.id}`}>
            <header><div><span className="overline" style={{color: p.color}}>{p.popular ? "POPULAR" : "TIER " + p.tier}</span><h3>{p.name}</h3><p>{p.tagline}</p></div>{p.popular && <Crown size={16} color={p.color}/>}</header>
            <div className="plan-prices">
              {CYCLES.map(c => (<div key={c.id}><small>{c.label}</small><b>{money(p.prices[c.id])}</b></div>))}
            </div>
            <div className="plan-meta">
              <span>Max branches <b>{p.max_branches}</b></span>
              <span>Features enabled <b>{p.features.length}</b></span>
            </div>
            <button className="admin-btn" onClick={() => openEdit(p)} data-testid={`edit-plan-${p.id}`}><Pencil size={13}/> Edit</button>
          </article>
        ))}
      </div>

      {editing && draft && (
        <div className="modal-backdrop">
          <div className="modal admin-modal" data-testid="plan-edit-modal">
            <div className="modal-head"><div><span className="overline">EDIT PLAN</span><h2>{draft.name}</h2></div><button className="icon-button" onClick={() => setEditing(null)}><X size={18}/></button></div>
            <label>Display name<input value={draft.name} onChange={e => setDraft({...draft, name: e.target.value})} data-testid="plan-name-input"/></label>
            <label>Tagline<textarea rows={2} value={draft.tagline} onChange={e => setDraft({...draft, tagline: e.target.value})}/></label>
            <div className="price-grid">
              {CYCLES.map(c => (
                <label key={c.id}>{c.label} (₹)<input type="number" value={draft.prices[c.id]} onChange={e => setDraft({...draft, prices: {...draft.prices, [c.id]: Number(e.target.value)}})} data-testid={`plan-price-${c.id}`}/></label>
              ))}
            </div>
            <div className="toggle-grid">
              <label>Max branches<input type="number" min={1} value={draft.max_branches} onChange={e => setDraft({...draft, max_branches: Number(e.target.value)})} data-testid="plan-max-branches"/></label>
              <label className="checkbox"><input type="checkbox" checked={!!draft.popular} onChange={e => setDraft({...draft, popular: e.target.checked})}/> Mark as "Popular"</label>
            </div>
            <div className="feature-sections">
              {Object.entries(groups).map(([group, feats]) => (
                <div key={group} className="feature-group">
                  <span className="overline">{group}</span>
                  <div className="feature-list">
                    {feats.map(f => (
                      <label key={f.key} className={draft.features.includes(f.key) ? "feat on" : "feat"}>
                        <input type="checkbox" checked={draft.features.includes(f.key)} onChange={() => toggleFeature(f.key)}/>
                        <span>{f.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {err && <div className="auth-alert error">{err}</div>}
            <div className="modal-actions">
              <button className="secondary-button" onClick={() => setEditing(null)} type="button">Cancel</button>
              <button className="primary-button" onClick={save} disabled={busy} type="button" data-testid="save-plan-button">{busy ? <Loader2 className="spin" size={14}/> : <Check size={14}/>} Save changes</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Tenants() {
  const [tenants, setTenants] = useState([]);
  useEffect(() => { api.get("/admin/tenants").then(r => setTenants(r.data)).catch(() => {}); }, []);
  return (
    <>
      <div className="admin-head"><div><span className="overline">TENANTS</span><h1>All restaurants on FynDine</h1><p>{tenants.length} total · newest first.</p></div></div>
      <div className="admin-table-wrap">
        <table className="admin-table" data-testid="admin-tenants-table">
          <thead><tr><th>Owner</th><th>Email</th><th>Outlet</th><th>City</th><th>GSTIN</th><th>WhatsApp</th><th>Plan</th><th>Trial / Active</th></tr></thead>
          <tbody>
            {tenants.map(t => (
              <tr key={t.id}>
                <td><b>{t.name || "—"}</b></td>
                <td>{t.email}</td>
                <td>{t.outlet || <span className="muted-label">Setup pending</span>}</td>
                <td>{t.outlet_city || "—"}</td>
                <td>{t.gstin || "—"}</td>
                <td data-testid={`admin-tenant-wa-${t.id}`}>{t.whatsapp?.connected ? `LIVE ${t.whatsapp.display_phone}${t.whatsapp.templates?.daily ? " · " + t.whatsapp.templates.daily : ""}` : "MOCK"}</td>
                <td>{t.active_plan ? <span className="pill accent">{t.active_plan}</span> : <span className="pill neutral">trial</span>}</td>
                <td className="muted-label">{t.active_until ? new Date(t.active_until).toLocaleDateString("en-IN") : (t.trial_ends_at ? "Trial ends " + new Date(t.trial_ends_at).toLocaleDateString("en-IN") : "—")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Payments() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/payments").then(r => setRows(r.data)).catch(() => {}); }, []);
  return (
    <>
      <div className="admin-head"><div><span className="overline">PAYMENTS</span><h1>Checkout log</h1><p>Every Razorpay order created and its settlement status.</p></div></div>
      <div className="admin-table-wrap">
        <table className="admin-table" data-testid="admin-payments-table">
          <thead><tr><th>Order</th><th>User</th><th>Plan</th><th>Cycle</th><th>Amount</th><th>Status</th><th>Created</th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.order_id}>
                <td><code>{r.order_id}</code></td>
                <td>{r.user_id.slice(0,8)}</td>
                <td>{r.plan_id}</td>
                <td>{r.cycle}</td>
                <td>{money(r.amount)}</td>
                <td><span className={`pill ${r.status === "paid" ? "success" : "neutral"}`}>{r.status}{r.mock ? " · mock" : ""}</span></td>
                <td className="muted-label">{new Date(r.created_at).toLocaleString("en-IN")}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={7} className="muted-label" style={{textAlign:"center",padding:28}}>No payment intents yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Settings() {
  const { user, refreshUser } = useAuth();
  const [email, setEmail] = useState(user?.email || "");
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(""); const [err, setErr] = useState("");

  const save = async (e) => {
    e.preventDefault(); setBusy(true); setErr(""); setMsg("");
    try {
      await api.post("/admin/change-credentials", { email, current_password: current, new_password: next || undefined });
      setMsg("Updated. Please sign in again with the new credentials next time.");
      setCurrent(""); setNext("");
      await refreshUser?.();
    } catch (e) { setErr(errorMessage(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div className="admin-head"><div><span className="overline">MY ACCOUNT</span><h1>Super-admin credentials</h1><p>Change the login email and password you use for the FynDine admin console.</p></div></div>
      <form onSubmit={save} className="admin-form">
        <label>Email<input type="email" required value={email} onChange={e => setEmail(e.target.value)} data-testid="admin-email-input"/></label>
        <label>Current password<input type="password" required value={current} onChange={e => setCurrent(e.target.value)} data-testid="admin-current-password"/></label>
        <label>New password <small>(leave blank to keep)</small><input type="password" value={next} onChange={e => setNext(e.target.value)} data-testid="admin-new-password"/></label>
        {err && <div className="auth-alert error">{err}</div>}
        {msg && <div className="auth-alert info">{msg}</div>}
        <button className="primary-button" disabled={busy} data-testid="admin-save-creds">{busy ? <Loader2 className="spin" size={14}/> : <Check size={14}/>} Save changes</button>
      </form>
    </>
  );
}

export default function AdminApp() {
  const { logout } = useAuth();
  const [page, setPage] = useState("overview");
  return (
    <div className="admin-shell" data-testid="admin-shell">
      <Nav page={page} setPage={setPage} logout={logout}/>
      <main className="admin-main">
        {page === "overview" && <Overview/>}
        {page === "plans" && <PlansEditor/>}
        {page === "tenants" && <Tenants/>}
        {page === "payments" && <Payments/>}
        {page === "settings" && <Settings/>}
      </main>
    </div>
  );
}
